/** @jsxImportSource @emotion/react */

import { Form as FinalForm } from 'react-final-form'
import arrayMutators from 'final-form-arrays'
import React, { FC, useMemo, useState } from 'react'
import { Button } from '@mui/material'
import { useMutation } from '@apollo/client'
import { InputField } from 'components/Input'
import { validateRequired } from 'utils/validation'
import { AdvancedFieldArray } from 'components/AdvancedFieldArray/AdvancedFieldArray'
import Editor from 'components/Editor'
import { Checkbox, CheckboxField } from 'components/Checkbox'
import { UPDATE_ADMISSION_SERVICE } from 'queries/admissionService'
import { ServiceCategoryTypeEnum } from '__generatedTypes__/globalTypes'
import { updateAdmissionService, updateAdmissionServiceVariables } from '__generatedTypes__/updateAdmissionService'
import { findAllAdmissionService_findAllAdmissionService_items as AdmissionService } from '__generatedTypes__/findAllAdmissionService'
import { PriceInput } from 'components/Input/PriceInput'
import { convertToCents, convertToDollars } from 'utils/priceFormatter'
import * as classes from 'pagesNew/UpgradePage/ProductPricing/styles'
import { toast } from 'utils/toast'

type Props = {
  data: AdmissionService
}

type FormValues = {
  id: string
  category: ServiceCategoryTypeEnum
  title: string
  isPopular: boolean
  price: number
  originalPrice: number
  feature: string[]
  paymentLink: string
  description: string | null
}

export const Form: FC<Props> = ({ data }) => {
  const [isDescriptionNeeded, setIsDescriptionNeeded] = useState(false)

  const [updateAdmissionService] = useMutation<updateAdmissionService, updateAdmissionServiceVariables>(
    UPDATE_ADMISSION_SERVICE
  )

  const handleSubmit = async (values: FormValues) => {
    const { price, originalPrice, description } = values

    try {
      await updateAdmissionService({
        variables: {
          input: {
            ...values,
            price: convertToCents(Number(price)),
            originalPrice: originalPrice != null ? convertToCents(Number(originalPrice)) : null,
            description: isDescriptionNeeded ? description : null,
          },
        },
      })

      toast.success('Admission service updated successfully')
    } catch (error) {
      toast.error('Failed to update admission service')
    }
  }

  const initialValues = useMemo(() => {
    const { id, category, title, isPopular, price, originalPrice, feature, paymentLink, description } = data

    setIsDescriptionNeeded(!!description)

    return {
      id,
      category,
      title,
      isPopular,
      price: convertToDollars(price),
      originalPrice: originalPrice != null ? convertToDollars(originalPrice) : undefined,
      feature,
      paymentLink,
      description,
    }
  }, [])

  return (
    <FinalForm<FormValues>
      mutators={{ ...arrayMutators }}
      onSubmit={handleSubmit}
      initialValues={initialValues}
      render={({ handleSubmit, submitting }) => (
        <form css={classes.form} onSubmit={handleSubmit} data-testid={`${initialValues.id}-form`}>
          <InputField name="title" validate={validateRequired} label="Title" placeholder="Enter Title" fullWidth />

          <CheckboxField name="isPopular" label="Most Popular Badge" css={classes.field} />

          <PriceInput
            name="price"
            validate={validateRequired}
            label="Sale Price"
            placeholder="Enter Sale Price"
            prefix="$"
            fullWidth
            css={classes.field}
          />

          <PriceInput
            name="originalPrice"
            label="Original Price"
            placeholder="Enter Original Price"
            prefix="$"
            fullWidth
            css={classes.field}
          />

          <AdvancedFieldArray<string>
            name="feature"
            validate={(value) => ((value?.length ?? 0) < 1 ? 'Should be at least one feature' : undefined)}
            itemName="Content"
            label="Features"
            getEmptyItem={() => ''}
            noItemsText="No features added"
            isSortable
            validateField={validateRequired}
            css={[classes.field, classes.fieldArray]}
            renderField={({ value, onChange, editable, index, error }) =>
              editable ? (
                <div css={classes.editorWrapper}>
                  <Editor
                    type="default"
                    value={value ?? ''}
                    onChange={onChange}
                    placeholder={`Enter Feature #${index + 1}`}
                    error={!!error}
                  />
                </div>
              ) : (
                <div dangerouslySetInnerHTML={{ __html: value ?? '' }} css={classes.editorValue} />
              )
            }
            removeConfirmation
          />

          <InputField
            name="paymentLink"
            validate={validateRequired}
            label="Payment link"
            placeholder="Enter Payment link"
            css={classes.field}
            fullWidth
          />

          <Checkbox
            name="isDescriptionNeeded"
            label="Do you need description?"
            checked={isDescriptionNeeded}
            onChange={() => setIsDescriptionNeeded((isDescriptionNeeded) => !isDescriptionNeeded)}
            css={classes.field}
          />

          {isDescriptionNeeded && (
            <InputField
              name="description"
              label="Description"
              placeholder="Enter Description"
              fullWidth
              multiline
              css={classes.textarea}
              validate={validateRequired}
            />
          )}

          <Button
            variant="outlined"
            type="submit"
            color="blue"
            size="large"
            fullWidth
            css={classes.submitButton}
            disabled={submitting}
          >
            Update
          </Button>
        </form>
      )}
    />
  )
}
